import { LOCATION_CHANGE } from "react-router-redux";
import User from "../services/User";
import Personalization from "../services/Personalization";
import entitlements from "./entitlements";
import userActions from "./user";
import GoogleTracking from "../services/GoogleTracking";
import appPath from "../utils/appPath";
import { browserHistory } from "react-router";
import { getNextScreenAfterSignIn, getNextScreenAfterCheckUser } from "../utils/getNextScreen";
import { SUCCESS_MESSAGES } from "common/constants/Constants";
import assign from "lodash.assign";
import Validation from "common/utils/Validation";
import { setErrorInErrors, clearErrorFromErrors, keepPersistedErrors } from "../utils/errorsUtil";
import ErrorMessages from "common/constants/ErrorMessages";
import Message from "utils/Message";
import { setThinking, doneThinking } from "./thinking";

const ERROR_MESSAGES = {
    EMAIL_OR_PASSWORD_ERROR: `Unable to create account. Try <a href=${ appPath("password/forgot") }>resetting your password</a> if you think this account exists already, or <a href="https://www.bloomberg.com/feedback" target="_blank">contact us</a> for further assistance.`
};

const {
    validateEmail,
    validateEmailsMatch,
    validateBloombergEmail,
    validatePasswordsMatch,
    validateStricterPasswordLength,
    validateBusinessweekId
} = Validation;

// Actions (must be name-spaced for reducer)

export const LOGIN_REGISTER_SHOW_ERROR = "LOGIN_REGISTER_SHOW_ERROR";
export const LOGIN_REGISTER_CLEAR_ERROR = "LOGIN_REGISTER_CLEAR_ERROR";
export const LOGIN_REGISTER_SHOW_SUCCESS = "LOGIN_REGISTER_SHOW_SUCCESS";
export const LOGIN_REGISTER_SET_CONFIRMATION_EMAIL = "LOGIN_REGISTER_SET_CONFIRMATION_EMAIL";
export const LOGIN_REGISTER_SET_PASSWORD = "LOGIN_REGISTER_SET_PASSWORD";
export const LOGIN_REGISTER_SET_CONFIRMATION_PASSWORD = "LOGIN_REGISTER_SET_CONFIRMATION_PASSWORD";
export const LOGIN_REGISTER_SET_INDUSTRY = "LOGIN_REGISTER_SET_INDUSTRY";
export const LOGIN_REGISTER_SET_JOB_TITLE = "LOGIN_REGISTER_SET_JOB_TITLE";
export const LOGIN_REGISTER_SET_SENIORITY = "LOGIN_REGISTER_SET_SENIORITY";
export const LOGIN_REGISTER_SET_BW_ACCOUNT_ID = "LOGIN_REGISTER_SET_BW_ACCOUNT_ID";
export const LOGIN_REGISTER_SET_REGION = "LOGIN_REGISTER_SET_REGION";
export const LOGIN_REGISTER_SET_POSTAL_CODE = "LOGIN_REGISTER_SET_POSTAL_CODE";
export const LOGIN_REGISTER_BUSINESSWEEK_UPDATE = "LOGIN_REGISTER_BUSINESSWEEK_UPDATE";
export const LOGIN_REGISTER_SHOW_PASSWORD_SUCCESS = "LOGIN_REGISTER_SHOW_PASSWORD_SUCCESS";
export const LOGIN_REGISTER_SET_FLOW = "LOGIN_REGISTER_SET_FLOW";
export const LOGIN_REGISTER_SET_EMAIL = "LOGIN_REGISTER_SET_EMAIL";
export const LOGIN_REGISTER_UPDATE_REAUTHORIZED = "LOGIN_REGISTER_UPDATE_REAUTHORIZED";
export const LOGIN_REGISTER_SHOW_RECAPTCHA = "SHOW_RECAPTCHA";
export const LOGIN_REGISTER_SHOW_ALERT_MODAL = "SHOW_ALERT_MODAL";
export const LOGIN_REGISTER_HIDE_ALERT_MODAL = "HIDE_ALERT_MODAL";

// Reducer

export function loginRegisterReducer(state = { flow: "register", email: "", confirmationEmail: "", useRecaptcha: false, errors: {} }, action = {}) {
    switch (action.type) {
        case LOCATION_CHANGE:
            return assign({}, {
                email: state.email,
                confirmationEmail: state.confirmationEmail,
                industry: state.industry,
                subscriptionId: state.subscriptionId,
                region: state.region,
                postalCode: state.postalCode,
                flow: state.flow,
                useRecaptcha: state.useRecaptcha,
                errors: keepPersistedErrors(state.errors)
            });
        case LOGIN_REGISTER_SHOW_RECAPTCHA:
            return assign({}, state, {
                useRecaptcha: action.useRecaptcha
            });
        case LOGIN_REGISTER_SHOW_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case LOGIN_REGISTER_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        case LOGIN_REGISTER_SHOW_SUCCESS:
            return assign({}, state, {
                successMessage: action.successMessage
            });
        case LOGIN_REGISTER_SHOW_PASSWORD_SUCCESS:
            return assign({}, state, {
                success: action.success
            });
        case LOGIN_REGISTER_SET_EMAIL:
            return assign({}, state, {
                email: action.email
            });
        case LOGIN_REGISTER_SET_CONFIRMATION_EMAIL:
            return assign({}, state, {
                confirmationEmail: action.confirmationEmail
            });
        case LOGIN_REGISTER_SET_PASSWORD:
            return assign({}, state, {
                password: action.password
            });
        case LOGIN_REGISTER_SET_CONFIRMATION_PASSWORD:
            return assign({}, state, {
                confirmationPassword: action.password
            });
        case LOGIN_REGISTER_SET_INDUSTRY:
            return assign({}, state, {
                industry: action.industry
            });
        case LOGIN_REGISTER_SET_JOB_TITLE:
            return assign({}, state, {
                jobTitle: action.jobTitle
            });
        case LOGIN_REGISTER_SET_SENIORITY:
            return assign({}, state, {
                seniority: action.seniority
            });
        case LOGIN_REGISTER_SET_BW_ACCOUNT_ID:
            return assign({}, state, {
                subscriptionId: action.subscriptionId
            });
        case LOGIN_REGISTER_SET_REGION:
            return assign({}, state, {
                region: action.region
            });
        case LOGIN_REGISTER_SET_POSTAL_CODE:
            return assign({}, state, {
                postalCode: action.postalCode
            });
        case LOGIN_REGISTER_BUSINESSWEEK_UPDATE:
            return assign({}, state, {
                email: action.email,
                subscriptionId: action.subscriptionId,
                region: action.region,
                postalCode: action.postalCode
            });
        case LOGIN_REGISTER_SHOW_ALERT_MODAL:
            return assign({}, state, {
                showAlertModal: true
            });
        case LOGIN_REGISTER_HIDE_ALERT_MODAL:
            return assign({}, state, {
                showAlertModal: false
            });
        case LOGIN_REGISTER_SET_FLOW:
            return assign({}, state, {
                flow: action.flow
            });
        case LOGIN_REGISTER_UPDATE_REAUTHORIZED:
            return assign({}, state, {
                reauthorized: action.reauthorized
            });
        default:
            return state;
    }
}

// Action Creators

export function showRecaptcha(useRecaptcha) {
    return {
        type: LOGIN_REGISTER_SHOW_RECAPTCHA,
        useRecaptcha
    };
}

export function trackSocialLogin(socialSite, isNew) {
    return (dispatch, getState) => {
        const { subscriptionForm = {} } = getState();

        if (isNew) {
            subscriptionForm.isActive ? GoogleTracking.setDataLayerFields({ cd9: "subscription-register" }) : null;
            GoogleTracking.setDataLayerFields({ action: "Register" });
            GoogleTracking.registerCompletionEvent(`join_now_${ socialSite }`, `join_now_${ socialSite }`);

            return;
        }

        subscriptionForm.isActive ? GoogleTracking.setDataLayerFields({ cd9: "subscription-login" }) : null;
        GoogleTracking.setDataLayerFields({ action: "Login" });
        GoogleTracking.registerSignInEvent(`sign_in_${ socialSite }`);
    };
}

export function trackEmailLogin() {
    GoogleTracking.setDataLayerFields({ action: "Login" });
    GoogleTracking.registerSignInEvent("sign_in_successful");
}

export function onLoginSuccess(user) {
    return (dispatch, getState) => {
        User.fetchUser().then((fetchedUser) => {
            if (fetchedUser.email && !fetchedUser.emailValidated && !fetchedUser.isSocialAccount) {
                User.resendConfirmationEmail();
            }

            dispatch(userActions.updateUserStore(fetchedUser));
        });

        Personalization.merge();

        const { subscriptionForm = {} } = getState();

        GoogleTracking.setDataLayerFields({
            cd13: subscriptionForm.isActive ? "subscription" : "sign_in"
        });

        user.socialSite ? dispatch(trackSocialLogin(user.socialSite, user.newSocialLogin)) : trackEmailLogin();

        return dispatch(entitlements.getSubscriptions())
            .then(() => user);
    };
}

export function showError(error) {
    return {
        type: LOGIN_REGISTER_SHOW_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: LOGIN_REGISTER_CLEAR_ERROR,
        errorType
    };
}

export function showSuccess(successMessage) {
    return (dispatch) => {
        dispatch({
            type: LOGIN_REGISTER_SHOW_SUCCESS,
            successMessage
        });
    };
}

export function setEmail(email) {
    return {
        type: LOGIN_REGISTER_SET_EMAIL,
        email
    };
}

export function setConfirmationEmail(confirmationEmail) {
    return {
        type: LOGIN_REGISTER_SET_CONFIRMATION_EMAIL,
        confirmationEmail
    };
}

export function setPassword(password) {
    return {
        type: LOGIN_REGISTER_SET_PASSWORD,
        password
    };
}

export function setConfirmationPassword(password) {
    return {
        type: LOGIN_REGISTER_SET_CONFIRMATION_PASSWORD,
        password
    };
}

export function setIndustry(industry) {
    return {
        type: LOGIN_REGISTER_SET_INDUSTRY,
        industry
    };
}

export function setJobTitle(jobTitle) {
    return {
        type: LOGIN_REGISTER_SET_JOB_TITLE,
        jobTitle
    };
}

export function setSeniority(seniority) {
    return {
        type: LOGIN_REGISTER_SET_SENIORITY,
        seniority
    };
}

export function setAccountId(subscriptionId) {
    return {
        type: LOGIN_REGISTER_SET_BW_ACCOUNT_ID,
        subscriptionId
    };
}

export function setRegion(region) {
    return {
        type: LOGIN_REGISTER_SET_REGION,
        region
    };
}

export function setPostalCode(postalCode) {
    return {
        type: LOGIN_REGISTER_SET_POSTAL_CODE,
        postalCode
    };
}

export function updateReauthorized(reauthorized) {
    return {
        type: LOGIN_REGISTER_UPDATE_REAUTHORIZED,
        reauthorized
    };
}

export function startUpdateProfession(professionInfo) {
    return () => {
        return User.updateProfession(professionInfo)
            .then(() => {
                GoogleTracking.registerCompletionEvent("submit_industry", "submit_industry");
            })
            .catch(() => {});
    };
}

export function showPasswordSuccess(success) {
    return (dispatch) => {
        dispatch({
            type: LOGIN_REGISTER_SHOW_PASSWORD_SUCCESS,
            success
        });
    };
}

export function login(email, password, captcha) {
    return (dispatch) => {
        return User.login(email, password, captcha)
            .then((user) => dispatch(onLoginSuccess(user)))
            .catch((error) => {
                switch (error.status) {
                    case 429:
                        dispatch(showRecaptcha(true));
                        break;
                    default:
                        dispatch(showError({
                            type: "login",
                            message: error.errorMessage
                        }));
                        break;
                }

                throw error;
            });
    };
}

export function register(email, captcha, redirectTo) {
    return (dispatch, getState) => {
        const { country } = getState();

        return User.register(email, country, captcha, redirectTo)
            .then(() => {
                if (appPath.isIframePath()) {
                    Message.userRegistered(email);
                }
            })
            .catch((error) => {
                switch (error.status) {
                    case 429:
                        dispatch(showRecaptcha(true));
                        break;
                    default:
                        dispatch(showError({
                            type: "register",
                            message: error.errorMessage
                        }));
                        break;
                }

                throw error;
            });
    };
}

export function registerBusinessweek(email, subscription) {
    return (dispatch) => {
        return User.registerWithSubscription(email, subscription)
            .then(({ success, errorMessage }) => {
                if (!success) {
                    dispatch(showError({
                        type: "linking",
                        persist: true,
                        message: errorMessage
                    }));
                }
            })
            .catch((error) => {
                const errorMessage = ERROR_MESSAGES[error.type] || error.errorMessage;

                dispatch(showError({
                    type: "register",
                    message: errorMessage
                }));

                throw error;
            });
    };
}

export function socialLogin(socialSite) {
    return (dispatch) => {
        return User.socialLogin(socialSite)
            .then((user) => dispatch(onLoginSuccess(user)))
            .catch((error) => {
                const errorType = error.errorType || error.type;

                dispatch(showAlertModal());
                dispatch(showError({
                    type: "socialLogin",
                    socialType: errorType,
                    message: error.errorMessage
                }));

                throw error;
            });
    };
}

export function validateSubscriptionEmails() {
    return (dispatch, getState) => {
        dispatch(checkIfValidSubscriptionEmail());
        dispatch(checkEmailsMatch());

        const {
            loginRegister: {
                errors
            }
        } = getState();

        return !errors["email"] && !errors["confirmEmail"];
    };
}

export function checkIfValidSubscriptionEmail() {
    return (dispatch, getState) => {
        const {
            loginRegister: {
                email
            }
        } = getState();

        const error = validateBloombergEmail(email) || validateEmail(email);

        if (error) {
            dispatch(showError({
                type: "email",
                message: error.message
            }));

            return;
        }

        dispatch(clearError("email"));
    };
}

export function checkIfValidEmail() {
    return (dispatch, getState) => {
        const {
            loginRegister: {
                email
            }
        } = getState();

        const error = validateEmail(email);

        if (error) {
            dispatch(showError({
                type: "email",
                message: error.message
            }));

            return;
        }

        dispatch(clearError("email"));
    };
}

export function checkEmailsMatch() {
    return (dispatch, getState) => {
        const {
            loginRegister: {
                email,
                confirmationEmail
            }
        } = getState();

        const error = validateEmailsMatch(email, confirmationEmail);

        if (error) {
            dispatch(showError({
                type: "confirmEmail",
                message: error.message
            }));

            return;
        }

        dispatch(clearError("confirmEmail"));
    };
}

export function emailChange(email) {
    return (dispatch) => {
        dispatch(setEmail(email));
        dispatch(checkIfValidEmail());
    };
}

export function subscriptionEmailChange(email) {
    return (dispatch) => {
        dispatch(setEmail(email));
        dispatch(checkIfValidSubscriptionEmail());
    };
}

export function confirmationEmailChange(confirmationEmail) {
    return (dispatch) => {
        dispatch(setConfirmationEmail(confirmationEmail));
        dispatch(checkEmailsMatch());
    };
}

export function checkIfValidAccountId() {
    return (dispatch, getState) => {
        const {
            loginRegister: {
                subscriptionId
            }
        } = getState();

        const error = validateBusinessweekId(subscriptionId);

        if (error) {
            dispatch(showError({
                type: "accountId",
                message: error.message
            }));

            return;
        }

        dispatch(clearError("accountId"));
    };
}

export function accountIdChange(accountId) {
    return (dispatch) => {
        dispatch(setAccountId(accountId));
        dispatch(checkIfValidAccountId());
    };
}

export function checkPasswordsMatch(password, confirmationPassword) {
    return (dispatch) => {
        const error = validatePasswordsMatch(password, confirmationPassword);

        if (confirmationPassword && error) {
            dispatch(showError({
                type: "confirmPassword",
                message: error.message
            }));
        } else {
            dispatch(clearError("confirmPassword"));
        }
    };
}

export function passwordChange(password) {
    return (dispatch, getState) => {
        dispatch(setPassword(password));
        dispatch(checkPasswordsMatch(password, getState().loginRegister.confirmationPassword));

        const error = validateStricterPasswordLength(password);

        if (error) {
            dispatch(showPasswordSuccess(null));
            dispatch(showError({
                type: "password",
                message: error.message
            }));
        } else {
            dispatch(clearError("password"));
            dispatch(showPasswordSuccess({
                type: "password",
                message: SUCCESS_MESSAGES.FORM_FIELD_VALIDATED
            }));
        }
    };
}

export function confirmationPasswordChange(confirmationPassword) {
    return (dispatch, getState) => {
        dispatch(setConfirmationPassword(confirmationPassword));
        dispatch(checkPasswordsMatch(getState().loginRegister.password, confirmationPassword));
    };
}

export function passwordSubmit(email, password, validationKey, isNewUser = false) {
    return (dispatch) => {
        return User.checkPassword(password)
            .then(() => {
                dispatch(showPasswordSuccess({
                    type: "password",
                    message: SUCCESS_MESSAGES.FORM_FIELD_VALIDATED
                }));

                return User.resetPassword(email, password, validationKey, isNewUser);
            })
            .catch((error) => {
                dispatch(showPasswordSuccess(null));

                // [LW] [ENG4SUBS-581] Invalid key errors display differently, with a special type and message
                if (error.errorMessage === ErrorMessages.RESET_PASSWORD_INVALID) {
                    dispatch(showError({
                        type: "setPasswordInvalidKey",
                        message: ErrorMessages.SET_PASSWORD_INVALID_KEY
                    }));

                    return;
                }

                dispatch(showError({
                    type: "setPassword",
                    message: error.errorMessage
                }));
            });
    };
}

export function showAlertModal() {
    return (dispatch) => {
        dispatch({ type: LOGIN_REGISTER_SHOW_ALERT_MODAL });
    };
}

export function hideAlertModal() {
    return (dispatch) => {
        dispatch({ type: LOGIN_REGISTER_HIDE_ALERT_MODAL });
    };
}

export function setFlow(flow) {
    return {
        type: LOGIN_REGISTER_SET_FLOW,
        flow
    };
}

export function redirectAfterCheckUser(user) {
    return () => {
        const screen = getNextScreenAfterCheckUser(user);

        if (!screen) {
            return;
        }

        browserHistory.push(appPath(screen));
    };
}

export function redirectAfterSignIn(user, location) {
    return () => {
        const screen = getNextScreenAfterSignIn(user, location);

        if (!screen) {
            return;
        }

        browserHistory.push(appPath.withoutQuery(screen));
    };
}

export function reauthorizeWithPassword(password, captcha) {
    return (dispatch, getState) => {
        dispatch(setThinking());
        const email = getState().user.email;

        return dispatch(login(email, password, captcha))
            .then(() => {
                dispatch(updateReauthorized(true));
                dispatch(clearError("reauthorize"));
                dispatch(doneThinking());
            })
            .catch((error) => {
                if (error.type === "ACCOUNT_LOCKED") {
                    User.logout();

                    return;
                }

                const message = error.type === "EMAIL_OR_PASSWORD_ERROR"
                    || error.errorMessage === "Password must be at least 6 characters."
                    ? "Invalid Password"
                    : error.errorMessage;

                dispatch(showError({
                    type: "reauthorize",
                    message
                }));

                dispatch(doneThinking());
            });
    };
}

export function reauthorizeFromUser() {
    return (dispatch) => {
        dispatch(setThinking());

        return dispatch(userActions.startFetchingUser())
            .then((user) => {
                if (user.socialSite) {
                    dispatch(updateReauthorized(true));
                }

                dispatch(doneThinking());
            })
            .catch((error) => {
                if (error.status >= 400 && error.status < 500) {
                    User.logout();
                }

                if (error.status >= 500) {
                    dispatch(showError({
                        type: "reauthorize",
                        message: "Unexpected error has occured, please try again"
                    }));
                }

                dispatch(doneThinking());
            });
    };
}

export default {
    loginRegisterReducer,
    showSuccess,
    emailChange,
    accountIdChange,
    setIndustry,
    setJobTitle,
    setSeniority,
    setAccountId,
    setRegion,
    setPostalCode,
    setPassword,
    setConfirmationPassword,
    startUpdateProfession,
    showRecaptcha,
    passwordChange,
    confirmationPasswordChange,
    setEmail,
    setConfirmationEmail,
    subscriptionEmailChange,
    login,
    register,
    socialLogin,
    showError,
    clearError,
    showPasswordSuccess,
    confirmationEmailChange,
    passwordSubmit,
    showAlertModal,
    hideAlertModal,
    setFlow,
    redirectAfterCheckUser,
    redirectAfterSignIn,
    registerBusinessweek,
    checkEmailsMatch,
    checkIfValidEmail,
    checkPasswordsMatch,
    checkIfValidSubscriptionEmail,
    validateSubscriptionEmails,
    reauthorizeFromUser,
    reauthorizeWithPassword
};
